import axios from 'axios';
import { PermissionType } from '@/enums/PermissionType';
import type { IBranch } from '@/interfaces/IBranch';
import type { IStats } from '@/interfaces/IStats';
import type { IEntityStats } from '@/interfaces/IEntityStats';
import { roundPrice } from '@/utils';
import type { IGpthelpResolvedHiddenReason } from '../../menu/gpthelp-admin-menu-visibility';
import { computeLocalBusinessDay } from '../../gpthelp-business-day';
import { toolCardId } from '../../cards/card-ids';
import {
  comparisonRange,
  statsPeriod,
  statsPeriodParams,
  type IStatsApiRange,
} from '../../cards/stats-comparison';
import {
  branchSalesCard,
  businessDayFootnote,
  channelsLabel,
  joinFootnotes,
  periodCaption,
  statsCard,
  topProductsCard,
  type IStatsSummary,
} from '../../cards/stats-cards';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
  type IGpthelpToolExecCtx,
} from '../tool-types';
import { toolResultEnvelope } from '../tool-envelope';
import {
  canOpenPage,
  DISPLAY_PARAM,
  LIST_DISPLAY_PARAM,
} from '../tool-display';
import {
  clampInt,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalString,
  getLimitArg,
  getOptionalStringArray,
  getPageArg,
  getRequiredNumberArray,
  getRequiredString,
  isRecord,
  normalizeStatsRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { axiosGetData, setIfDefined, statsUrl } from '../tool-http';
import { ensurePermissionOrThrow } from '../tool-scope';
import { accessibleBranches } from '../tool-gate';
import { capList, omitNullish } from '../sanitize';
import { buildMenuToolHelpers } from './menu-tools';
import {
  STOCK_MOVEMENT_TYPES_DESCRIPTION,
  getOptionalStockMovementTypes,
} from './stock-shared';
import { knownStockCost } from './stock-costs';

const ensureStatsPermission = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.StatisticsView,
    message: 'No tiene permisos para ver estadísticas',
  });

/** Shared doc for the dateMode param carried by all stats_* tools. */
export const STATS_DATE_MODE_DESCRIPTION =
  "Date bucketing. 'business' (default) groups by business day using the branch cutoff hour - same as the stats pages' default. 'calendar' groups by plain calendar date - same as the stats pages' Calendar toggle. Use 'calendar' only when the user explicitly asks for calendar days or is reading a stats page with the toggle on Calendar.";

const DATE_MODE_PARAM = {
  type: 'string',
  enum: ['business', 'calendar'],
  default: 'business',
  description: STATS_DATE_MODE_DESCRIPTION,
};

const STATS_DATE_MODES = ['business', 'calendar'] as const;

const STATS_FROM_PARAM = {
  type: 'string',
  description: 'Start: a date (yyyy-MM-dd) or an ISO instant.',
};

const STATS_TO_PARAM = { type: 'string', description: RANGE_END_DESCRIPTION };

/** The hour a stats day starts at: the branch's business-day start, 0 in calendar mode. */
const statsStartHour = (
  args: Record<string, unknown>,
  deps: ICartadigitalAdminHostToolDeps,
): number =>
  getOptionalEnum(args, 'dateMode', STATS_DATE_MODES) === 'calendar'
    ? 0
    : deps.restaurantStore.selectedBranch?.businessDayStartHour ?? 0;

/** from/to/interval of a stats_* call as the stats API reads them (see normalizeStatsRange). */
const getStatsRangeParams = (
  args: Record<string, unknown>,
  deps: ICartadigitalAdminHostToolDeps,
  timeZone: string,
  interval: string | null = null,
) =>
  normalizeStatsRange({
    from: getRequiredString(args, 'from'),
    to: getOptionalString(args, 'to'),
    timeZone,
    businessDayStartHour: statsStartHour(args, deps),
    interval,
  });

const sumGroup = (stats: IStats, id: string): number =>
  (stats.groups.find((group) => group.id === id)?.stats ?? []).reduce(
    (sum, stat) => sum + (Number.isFinite(stat.value) ? stat.value : 0),
    0,
  );

/**
 * The totals of a `/stats/orders` answer fetched with `includeNonMonetary`:
 * sales net of non-monetary payments (invitaciones, consumo empleado), like
 * the dashboard; comped orders still count as orders.
 */
export function summarizeOrderStats(stats: IStats): IStatsSummary {
  const grossSales = roundPrice(sumGroup(stats, 'total'));
  const nonMonetary = roundPrice(sumGroup(stats, 'nonMonetaryTotal'));
  const sales = roundPrice(Math.max(0, grossSales - nonMonetary));
  const orders = sumGroup(stats, 'count');
  return {
    sales,
    grossSales,
    nonMonetary,
    orders,
    averageTicket: orders > 0 ? roundPrice(sales / orders) : 0,
  };
}

/**
 * Appends calendar=1 only in calendar mode — business mode keeps the URL
 * byte-identical to the pre-dateMode behavior (and to what the pages send).
 */
const applyDateMode = (
  qs: URLSearchParams,
  args: Record<string, unknown>,
): void => {
  const mode = getOptionalEnum(args, 'dateMode', STATS_DATE_MODES);
  if (mode === 'calendar') qs.set('calendar', '1');
};

/** One `/stats/orders` request of `stats_orders`: a branch, a range and the call's filters. */
interface IOrderStatsQuery {
  restaurantId: number;
  branchId: number;
  range: IStatsApiRange;
  orderTypes: string[] | null;
  heatmap: boolean;
}

/**
 * Always with the non-monetary breakdown: the summary nets it out, so the
 * assistant's sales match the dashboard's.
 */
const fetchOrderStats = (
  query: IOrderStatsQuery,
  args: Record<string, unknown>,
): Promise<IStats> => {
  const qs = new URLSearchParams();
  qs.set('r', String(query.restaurantId));
  qs.set('b', String(query.branchId));
  qs.set('from', query.range.from);
  setIfDefined(qs, 'to', query.range.to);
  if (query.orderTypes?.length) qs.set('types', query.orderTypes.join(','));
  setIfDefined(qs, 'interval', query.range.interval);
  if (query.heatmap) qs.set('aggregate', 'weekly');
  applyDateMode(qs, args);
  qs.set('includeNonMonetary', '1');
  return axiosGetData<IStats>(`${statsUrl('/orders')}?${qs.toString()}`);
};

/** Branches `stats_orders.allBranches` reads, the first ones by name (plan §8 R2). */
const ALL_BRANCHES_MAX = 20;
/** Branches whose stats are read at the same time. */
const ALL_BRANCHES_CONCURRENCY = 4;

/** A branch's sales in the period, as the summary counts them. */
interface IBranchOrderStats {
  branchId: number;
  name: string;
  sales: number;
  grossSales: number;
  orders: number;
  averageTicket: number;
}

/** A branch whose stats the API does not show this member. */
interface IBranchNoAccess {
  branchId: number;
  name: string;
  noAccess: true;
}

type TBranchOrderStats = IBranchOrderStats | IBranchNoAccess;

const answered = (row: TBranchOrderStats): row is IBranchOrderStats =>
  !('noAccess' in row);

/**
 * The API checks StatisticsView in each branch: it refuses one the member
 * has no access to with 401 (a missing permission) or 403.
 */
function isNoAccess(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
}

async function branchOrderStats(
  branch: IBranch,
  read: (branchId: number) => Promise<IStats>,
): Promise<TBranchOrderStats> {
  try {
    const summary = summarizeOrderStats(await read(branch.id));
    return {
      branchId: branch.id,
      name: branch.name,
      sales: summary.sales,
      grossSales: summary.grossSales,
      orders: summary.orders,
      averageTicket: summary.averageTicket,
    };
  } catch (error) {
    if (isNoAccess(error)) {
      return { branchId: branch.id, name: branch.name, noAccess: true };
    }
    throw error;
  }
}

/** The sum of the branches that answered; the average ticket of that sum. */
function branchesTotal(
  rows: TBranchOrderStats[],
): Omit<IBranchOrderStats, 'branchId' | 'name'> {
  const counted = rows.filter(answered);
  const sales = roundPrice(counted.reduce((sum, row) => sum + row.sales, 0));
  const grossSales = roundPrice(
    counted.reduce((sum, row) => sum + row.grossSales, 0),
  );
  const orders = counted.reduce((sum, row) => sum + row.orders, 0);
  return {
    sales,
    grossSales,
    orders,
    averageTicket: orders > 0 ? roundPrice(sales / orders) : 0,
  };
}

/**
 * `stats_orders` with `allBranches` (plan §8 R2): the summary of each active
 * branch the member works in (the first 20 by name), with the request the
 * current branch would get minus the series (the API resolves each branch's
 * business day), read four at a time, and their total.
 */
async function ordersByBranch(
  args: Record<string, unknown>,
  { deps, scope, gate, turn, fmt }: IGpthelpToolExecCtx,
) {
  const orderTypes = getOptionalStringArray(args, 'orderTypes');
  const range = getStatsRangeParams(args, deps, scope.timeZone);
  const active = accessibleBranches(deps)
    .filter((branch) => branch.active)
    .sort((a, b) => a.name.localeCompare(b.name, fmt.locale));
  const listed = capList(active, ALL_BRANCHES_MAX);
  const read = (branchId: number) =>
    fetchOrderStats(
      {
        restaurantId: scope.restaurantId,
        branchId,
        range,
        orderTypes,
        heatmap: false,
      },
      args,
    );

  const byBranch: TBranchOrderStats[] = [];
  for (
    let start = 0;
    start < listed.results.length;
    start += ALL_BRANCHES_CONCURRENCY
  ) {
    const chunk = listed.results.slice(start, start + ALL_BRANCHES_CONCURRENCY);
    byBranch.push(
      ...(await Promise.all(
        chunk.map((branch) => branchOrderStats(branch, read)),
      )),
    );
  }

  const data = omitNullish({
    byBranch,
    total: branchesTotal(byBranch),
    truncated: listed.truncated ?? null,
  });
  // No branch to compare: the answer is in words.
  if (!getOptionalBoolean(args, 'display') || !byBranch.length) return data;

  const currentDate = computeLocalBusinessDay({
    timeZone: scope.timeZone,
    startHour: statsStartHour(args, deps),
  }).currentBusinessDate;
  return toolResultEnvelope({
    data,
    cards: [
      branchSalesCard(
        {
          id: toolCardId('stats-branches', turn.requestId),
          rows: byBranch.map((row) => ({
            name: row.name,
            summary: answered(row) ? row : null,
          })),
          // The period, the channels when filtered and, past 20 branches,
          // how many the table lists: never total sales.
          footnote: joinFootnotes([
            channelsLabel(orderTypes, fmt),
            periodCaption(statsPeriod(range), currentDate, fmt),
            listed.truncated
              ? fmt.t('assistant.stats.branchesShown', {
                  shown: fmt.number(listed.results.length),
                  total: fmt.number(active.length),
                })
              : null,
          ]),
          showStatsPage: canOpenPage(gate, 'stats-orders'),
        },
        fmt,
      ),
    ],
  });
}

export const statsTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'stats_orders',
      description:
        'Fetch orders stats for the currently selected restaurant + branch. Returns summary (sales = net of non-monetary sales like the dashboard, grossSales, nonMonetary, orders, averageTicket) plus the time-series (total = gross, count, average, nonMonetaryTotal) or weekly heatmap (dayOfWeek/hour).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          orderTypes: {
            type: 'array',
            items: { type: 'string', enum: ['seat', 'away', 'delivery'] },
            description: 'Optional filter by order type.',
          },
          aggregate: {
            type: 'string',
            enum: ['timeseries', 'weeklyHeatmap'],
            default: 'timeseries',
          },
          dateMode: DATE_MODE_PARAM,
          allBranches: {
            type: 'boolean',
            description:
              'true = one summary per branch you can see (up to 20) and their total, which covers only the branches listed; no series.',
          },
          display: DISPLAY_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, ctx) {
      const { deps, scope, gate, turn, fmt } = ctx;
      ensureStatsPermission(deps);
      if (getOptionalBoolean(args, 'allBranches')) {
        return await ordersByBranch(args, ctx);
      }

      const aggregate = getOptionalString(args, 'aggregate') ?? 'timeseries';
      const orderTypes = getOptionalStringArray(args, 'orderTypes');
      const heatmap = aggregate === 'weeklyHeatmap';

      const range = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        heatmap ? 'weekly' : getOptionalString(args, 'interval'),
      );

      const fetchOrders = (params: IStatsApiRange) =>
        fetchOrderStats(
          {
            restaurantId: scope.restaurantId,
            branchId: scope.branchId,
            range: params,
            orderTypes,
            heatmap,
          },
          args,
        );

      const stats = await fetchOrders(range);
      const summary = summarizeOrderStats(stats);
      const data = { ...stats, summary };
      if (!getOptionalBoolean(args, 'display')) return data;

      const period = statsPeriod(range);
      const startHour = statsStartHour(args, deps);
      const currentDate = computeLocalBusinessDay({
        timeZone: scope.timeZone,
        startHour,
      }).currentBusinessDate;
      const compared = comparisonRange(period, currentDate);
      // A failed comparison only drops the deltas.
      const previous = compared
        ? await fetchOrders({ ...statsPeriodParams(compared), interval: null })
            .then(summarizeOrderStats)
            .catch(() => null)
        : null;
      const oneDay = !period.partial && period.first === period.last;

      return toolResultEnvelope({
        data,
        cards: [
          statsCard(
            {
              id: toolCardId('stats', turn.requestId),
              summary,
              previous,
              // A channel filter is named: the tiles never read as total sales.
              footnote: joinFootnotes([
                channelsLabel(orderTypes, fmt),
                businessDayFootnote(period, startHour, fmt),
              ]),
              showStatsPage: canOpenPage(gate, 'stats-orders'),
            },
            fmt,
          ),
        ],
        followUps: [
          oneDay ? fmt.t('assistant.followUp.compare') : '',
          fmt.t('assistant.followUp.byHour'),
        ],
      });
    },
  },
  {
    definition: {
      name: 'stats_covers',
      description:
        'Fetch covers (guest count) stats: total covers, sittings, average party size, spend per cover, average table turn minutes.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);
      applyDateMode(qs, args);

      return await axiosGetData<IStats>(`${statsUrl('/covers')}?${qs.toString()}`);
    },
  },
  {
    definition: {
      name: 'stats_views',
      description:
        'Fetch views stats for the currently selected restaurant + branch. Returns time-series (links/tables/otherQr) or weekly heatmap.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          aggregate: {
            type: 'string',
            enum: ['timeseries', 'weeklyHeatmap'],
            default: 'timeseries',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const aggregate = getOptionalString(args, 'aggregate') ?? 'timeseries';
      const heatmap = aggregate === 'weeklyHeatmap';

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        heatmap ? 'weekly' : getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);

      setIfDefined(qs, 'interval', interval);
      if (heatmap) qs.set('aggregate', 'weekly');
      applyDateMode(qs, args);

      return await axiosGetData<IStats>(`${statsUrl('/views')}?${qs.toString()}`);
    },
  },
  {
    definition: {
      name: 'stats_paymentMethods',
      description:
        'Fetch payment method stats. Includes total/count and per-method paid amount series. Can optionally include per-method order counts and per-method order total sums.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          orderTypes: {
            type: 'array',
            items: { type: 'string', enum: ['seat', 'away', 'delivery'] },
          },
          includeMethodCounts: {
            type: 'boolean',
            default: true,
            description: 'When true, includes count_<method> series.',
          },
          includeMethodOrderTotals: {
            type: 'boolean',
            default: true,
            description: 'When true, includes ordersTotal_<method> series.',
          },
          methodCountMode: {
            type: 'string',
            enum: ['used', 'primary'],
            default: 'used',
            description:
              'used: order counted for method if it has non-zero paid amount for that method (split payments). primary: use order.payment_method only.',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const orderTypes = getOptionalStringArray(args, 'orderTypes');
      const includeMethodCounts = getOptionalBoolean(args, 'includeMethodCounts');
      const includeMethodOrderTotals = getOptionalBoolean(
        args,
        'includeMethodOrderTotals',
      );
      const methodCountMode = getOptionalString(args, 'methodCountMode') ?? 'used';

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);

      if (orderTypes?.length) qs.set('types', orderTypes.join(','));

      // Defaults to true for GPTHelp to enable avg-per-method computations.
      setIfDefined(qs, 'includeMethodCounts', (includeMethodCounts ?? true) ? 1 : 0);
      setIfDefined(
        qs,
        'includeMethodOrderTotals',
        (includeMethodOrderTotals ?? true) ? 1 : 0,
      );
      setIfDefined(qs, 'methodCountMode', methodCountMode);
      applyDateMode(qs, args);

      return await axiosGetData<IStats>(`${statsUrl('/paymentMethods')}?${qs.toString()}`);
    },
  },
  {
    definition: {
      name: 'stats_tips',
      description:
        'Fetch tips stats. mode=total returns total/count/average; mode=payment returns totals by payment method.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          mode: {
            type: 'string',
            enum: ['total', 'payment'],
            default: 'total',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const mode = getOptionalString(args, 'mode') ?? 'total';

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);
      setIfDefined(qs, 'mode', mode);
      applyDateMode(qs, args);

      return await axiosGetData<IStats>(`${statsUrl('/tips')}?${qs.toString()}`);
    },
  },
  {
    definition: {
      name: 'stats_products_rank',
      description:
        "Rank products by units/revenue/avg unit price for the selected period. Results may include items that are currently disabled or even deleted (they had sales). Disabled items with ZERO sales in the period are absent entirely, so 'least sold' answers may miss them; use menu_findItems to enumerate hidden items. Page with page/limit; totalItems counts every row.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          metric: {
            type: 'string',
            enum: ['units', 'revenue', 'avgUnitPrice'],
            default: 'units',
          },
          order: { type: 'string', enum: ['desc', 'asc'], default: 'desc' },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 100, default: 10 },
          dateMode: DATE_MODE_PARAM,
          display: LIST_DISPLAY_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope, gate, turn, fmt }) {
      ensureStatsPermission(deps);
      const helpers = buildMenuToolHelpers(deps);

      const metric = getOptionalString(args, 'metric') ?? 'units';
      const order = getOptionalString(args, 'order') ?? 'desc';
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 10, max: 100 });

      const range = getStatsRangeParams(args, deps, scope.timeZone);
      const { from, to } = range;

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      applyDateMode(qs, args);

      const data = await axiosGetData<IEntityStats<any>>(
        `${statsUrl('/products')}?${qs.toString()}`,
      );

      const salesGroup = data.groups.find((g) => g.id === 'sales' || g.title === 'sales');
      const incomeGroup = data.groups.find((g) => g.id === 'income' || g.title === 'income');
      const avgGroup = data.groups.find((g) => g.id === 'average' || g.title === 'average');

      if (!salesGroup || !incomeGroup || !avgGroup) {
        throw new HostToolError('UNEXPECTED_RESPONSE', 'Unexpected products stats response');
      }

      const byId = new Map<number, { units: number; revenue: number; avgUnitPrice: number }>();
      for (const stat of salesGroup.stats) {
        if (typeof stat.id !== 'number') continue;
        byId.set(stat.id, { units: stat.value, revenue: 0, avgUnitPrice: 0 });
      }
      for (const stat of incomeGroup.stats) {
        if (typeof stat.id !== 'number') continue;
        const existing = byId.get(stat.id) ?? { units: 0, revenue: 0, avgUnitPrice: 0 };
        existing.revenue = stat.value;
        byId.set(stat.id, existing);
      }
      for (const stat of avgGroup.stats) {
        if (typeof stat.id !== 'number') continue;
        const existing = byId.get(stat.id) ?? { units: 0, revenue: 0, avgUnitPrice: 0 };
        existing.avgUnitPrice = stat.value;
        byId.set(stat.id, existing);
      }

      interface IGpthelpProductsRankRow {
        itemId: number;
        name: string | null;
        picture: string | null;
        units: number;
        revenue: number;
        avgUnitPrice: number;
        notInCatalog?: true;
        hiddenNow?: IGpthelpResolvedHiddenReason[];
      }

      const rows: IGpthelpProductsRankRow[] = Array.from(byId.entries()).map(([id, v]) => ({
        itemId: id,
        name: data.entities?.[id]?.name ?? null,
        picture: data.entities?.[id]?.picture ?? null,
        units: v.units,
        revenue: v.revenue,
        avgUnitPrice: v.avgUnitPrice,
      }));

      const selector = (row: IGpthelpProductsRankRow) => {
        switch (metric) {
          case 'revenue':
            return row.revenue;
          case 'avgUnitPrice':
            return row.avgUnitPrice;
          default:
            return row.units;
        }
      };

      // Each page is a fresh API call whose tie order isn't guaranteed: the itemId
      // tiebreak keeps page boundaries stable.
      rows.sort((a, b) => {
        const diff = selector(b) - selector(a);
        return (order === 'asc' ? -diff : diff) || a.itemId - b.itemId;
      });

      const results = rows.slice((page - 1) * limit, page * limit);
      for (const row of results) {
        helpers.enrichStatsEntryWithVisibility(row, row.itemId);
      }

      const ranking = {
        metric,
        order,
        page,
        limit,
        totalItems: rows.length,
        results,
      };
      // The card is «Más vendidos» from the top, of products that sold: the
      // least sold, the average price ranking, later pages and a period
      // without sales are answered in words.
      const sold = results.filter((row) => row.units > 0 || row.revenue > 0);
      const topSellers =
        order === 'desc' && metric !== 'avgUnitPrice' && page === 1;
      if (!getOptionalBoolean(args, 'display') || !topSellers || !sold.length)
        return ranking;

      const period = statsPeriod(range);
      const startHour = statsStartHour(args, deps);
      const currentDate = computeLocalBusinessDay({
        timeZone: scope.timeZone,
        startHour,
      }).currentBusinessDate;

      return toolResultEnvelope({
        data: ranking,
        cards: [
          topProductsCard(
            {
              id: toolCardId('top-products', turn.requestId),
              rows: sold.map((row) => ({
                name: row.name ?? `#${row.itemId}`,
                units: row.units,
                revenue: row.revenue,
              })),
              caption: periodCaption(period, currentDate, fmt),
              footnote: businessDayFootnote(period, startHour, fmt),
              showStatsPage: canOpenPage(gate, 'stats-products'),
            },
            fmt,
          ),
        ],
      });
    },
  },
  {
    definition: {
      name: 'stats_products_series',
      description:
        'Fetch per-item time series (units + revenue) for selected items over a period. Accepts any itemIds, including disabled items.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          itemIds: {
            type: 'array',
            items: { type: 'number' },
            minItems: 1,
            maxItems: 20,
          },
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          orderTypes: {
            type: 'array',
            items: { type: 'string', enum: ['seat', 'away', 'delivery'] },
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from', 'itemIds'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);
      const helpers = buildMenuToolHelpers(deps);

      const orderTypes = getOptionalStringArray(args, 'orderTypes');
      const itemIdsRaw = getRequiredNumberArray(args, 'itemIds');
      const itemIds = Array.from(
        new Set(itemIdsRaw.map((n) => Math.floor(n)).filter((n) => n > 0)),
      );

      if (!itemIds.length) {
        throw new HostToolError('INVALID_ARGS', 'itemIds must contain positive integers', {
          itemIds: itemIdsRaw,
        });
      }
      if (itemIds.length > 20) {
        throw new HostToolError('INVALID_ARGS', 'Too many itemIds (max 20)', {
          count: itemIds.length,
        });
      }

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);
      setIfDefined(qs, 'itemIds', itemIds.join(','));
      if (orderTypes?.length) qs.set('types', orderTypes.join(','));
      applyDateMode(qs, args);

      interface IGpthelpProductsSeriesEntity {
        id: number;
        name: string | null;
        picture: string | null;
        categoryId: number | null;
        categoryName: string | null;
        notInCatalog?: true;
        hiddenNow?: IGpthelpResolvedHiddenReason[];
      }

      interface IGpthelpProductsSeriesResponse {
        interval: string;
        timezone: string;
        entities: Record<number, IGpthelpProductsSeriesEntity>;
        points: unknown[];
      }

      const data = await axiosGetData<IGpthelpProductsSeriesResponse>(
        `${statsUrl('/productsSeries')}?${qs.toString()}`,
      );

      if (isRecord(data.entities)) {
        for (const key of Object.keys(data.entities)) {
          const entityId = Number(key);
          const entity = data.entities[entityId];
          if (!entity) continue;
          helpers.enrichStatsEntryWithVisibility(entity, entityId);
        }
      }

      return data;
    },
  },
  {
    definition: {
      name: 'stats_stockConsumption_rank',
      description:
        "Rank stock items by quantity consumed and added in the period (stock movements), with each item's CURRENT unitCost (null = not loaded). Stock usage, not cost of sales: for cost of sales multiply units sold by stock_getItemCosts.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          movementTypes: {
            type: 'array',
            items: { type: 'number' },
            description: STOCK_MOVEMENT_TYPES_DESCRIPTION,
          },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const movementTypes = getOptionalStockMovementTypes(args, 'movementTypes');
      const limitRaw = getOptionalNumber(args, 'limit') ?? 20;
      const limit = clampInt(limitRaw, 1, 50);

      const { from, to } = getStatsRangeParams(args, deps, scope.timeZone);

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);

      if (movementTypes?.length) qs.set('types', movementTypes.join(','));
      applyDateMode(qs, args);

      const data = await axiosGetData<IEntityStats<any>>(
        `${statsUrl('/stock-consumption')}?${qs.toString()}`,
      );
      const consumptionGroup = data.groups.find(
        (g) => g.id === 'consumption' || g.title === 'consumption',
      );
      const additionsGroup = data.groups.find(
        (g) => g.id === 'additions' || g.title === 'additions',
      );
      if (!consumptionGroup || !additionsGroup) {
        throw new HostToolError('UNEXPECTED_RESPONSE', 'Unexpected stock stats response');
      }

      const additionsById = new Map<number, number>();
      for (const stat of additionsGroup.stats) {
        if (typeof stat.id !== 'number') continue;
        additionsById.set(stat.id, stat.value);
      }

      const rows = (consumptionGroup.stats ?? [])
        .filter((s) => typeof s.id === 'number')
        .map((s) => {
          const entity = data.entities?.[s.id!] ?? null;
          return {
            stockItemId: s.id,
            name: entity?.name ?? null,
            unit: entity?.unit ?? null,
            unitCost: knownStockCost(entity?.unitCost),
            consumption: s.value,
            additions: additionsById.get(s.id!) ?? 0,
          };
        })
        .sort((a, b) => b.consumption - a.consumption);

      return {
        limit,
        totalItems: rows.length,
        results: rows.slice(0, limit),
      };
    },
  },
  {
    definition: {
      name: 'stats_categories',
      description:
        "Category sales rollup for the period (the stats Categories page): net/gross/discount/quantity per category with % of total. dimension 'reporting' uses the reporting-category tree (2 levels), 'menu' uses flat menu categories. Set includeItems to drill into per-item rows inside each category (capped at 15 per row).",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          dimension: {
            type: 'string',
            enum: ['reporting', 'menu'],
            default: 'reporting',
          },
          includeItems: {
            type: 'boolean',
            default: false,
            description:
              'Include per-item drill rows inside each category (max 15 per row, itemsTruncated flags more).',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const dimension =
        getOptionalEnum(args, 'dimension', ['reporting', 'menu'] as const) ??
        'reporting';
      const includeItems = getOptionalBoolean(args, 'includeItems') ?? false;

      const { from, to } = getStatsRangeParams(args, deps, scope.timeZone);

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      qs.set('dimension', dimension);
      applyDateMode(qs, args);

      interface ICategorySalesToolItemRow {
        itemId: number;
        name: string | null;
        quantity: number;
        gross: number;
        discountAllocated: number;
        net: number;
      }
      interface ICategorySalesToolRow {
        id: number | null;
        name: string;
        source: string;
        quantity: number;
        gross: number;
        discountAllocated: number;
        net: number;
        pctOfTotal: number;
        pctOfParent: number;
        children?: ICategorySalesToolRow[];
        items?: ICategorySalesToolItemRow[];
        itemsTruncated?: true;
      }
      interface ICategorySalesToolResponse {
        dimension: string;
        rows: ICategorySalesToolRow[];
        totals: Record<string, number>;
      }

      const data = await axiosGetData<ICategorySalesToolResponse>(
        `${statsUrl('/categories')}?${qs.toString()}`,
      );

      const ITEMS_CAP = 15;
      const shapeRow = (row: ICategorySalesToolRow): ICategorySalesToolRow => {
        const { items, children, ...rest } = row;
        const shaped: ICategorySalesToolRow = { ...rest };
        if (includeItems && items?.length) {
          shaped.items = items.slice(0, ITEMS_CAP);
          if (items.length > ITEMS_CAP) shaped.itemsTruncated = true;
        }
        if (children?.length) shaped.children = children.map(shapeRow);
        return shaped;
      };

      return {
        dimension: data.dimension,
        totals: data.totals,
        rows: (data.rows ?? []).map(shapeRow),
      };
    },
  },
  {
    definition: {
      name: 'stats_clubTopups',
      description:
        'Club top-ups over time: money loaded onto club member balances. mode=total returns principal + gifted-bonus series; mode=payment splits principal by payment method. Top-ups are NOT sales revenue (they are liability created; spending happens later via the club payment method).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          mode: {
            type: 'string',
            enum: ['total', 'payment'],
            default: 'total',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const mode =
        getOptionalEnum(args, 'mode', ['total', 'payment'] as const) ?? 'total';

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);
      qs.set('mode', mode);
      applyDateMode(qs, args);

      return await axiosGetData<IStats>(`${statsUrl('/club-topups')}?${qs.toString()}`);
    },
  },
  {
    definition: {
      name: 'stats_itemOptions',
      description:
        "Aggregate sales of menu-item OPTIONS - variants (size/flavor choices) and paid additionals/toppings. The ONLY tool for 'how many X were sold WITH option Y', 'top additionals by revenue', 'attach rate of topping Z on item W'. units/revenue already account for parent line quantities. IMPORTANT: option revenue is already included inside item revenue (stats_products_rank line totals contain options) - never sum the two. Rows with isVariant:true are price variants whose revenue IS the item's base price, not an add-on - for 'revenue from extras' use kind:'additionals' and never mix variant and additional revenue. attachRatePct (only with itemId) = % of the item's sold units that carried the option. Options with zero sales in the period are absent - enumerate an item's configured options via menu_getItem; resolve ids via menu_findItems/menu_getItem first. Names come from the current catalog; deleted:true marks option items no longer configured. Finished & paid orders only; canceled lines excluded; max 93 days per call. For trends over time use stats_itemOptionsSeries.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          itemId: {
            type: 'number',
            description:
              "Filter to one menu item; also returns that item's totalUnits and per-row attachRatePct.",
          },
          optionId: {
            type: 'number',
            description: 'One option group, e.g. Toppings.',
          },
          optionItemId: {
            type: 'number',
            description: 'One specific choice, e.g. Nutella.',
          },
          kind: {
            type: 'string',
            enum: ['all', 'variants', 'additionals'],
            default: 'all',
          },
          perItem: {
            type: 'boolean',
            default: false,
            description: 'Split rows per parent menu item.',
          },
          sort: { type: 'string', enum: ['units', 'revenue'], default: 'units' },
          page: {
            type: 'number',
            minimum: 1,
            default: 1,
            description: 'Request the next page only when truncated:true.',
          },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 15 },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const itemId = getOptionalNumber(args, 'itemId');
      const optionId = getOptionalNumber(args, 'optionId');
      const optionItemId = getOptionalNumber(args, 'optionItemId');
      const kind =
        getOptionalEnum(args, 'kind', ['all', 'variants', 'additionals'] as const) ??
        'all';
      const perItem = getOptionalBoolean(args, 'perItem') ?? false;
      const sort =
        getOptionalEnum(args, 'sort', ['units', 'revenue'] as const) ?? 'units';
      const pageRaw = getOptionalNumber(args, 'page') ?? 1;
      const page = clampInt(pageRaw, 1, 100000);
      const limitRaw = getOptionalNumber(args, 'limit') ?? 15;
      const limit = clampInt(limitRaw, 1, 50);

      const { from, to } = getStatsRangeParams(args, deps, scope.timeZone);

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'itemId', itemId !== null ? Math.floor(itemId) : null);
      setIfDefined(qs, 'optionId', optionId !== null ? Math.floor(optionId) : null);
      setIfDefined(
        qs,
        'optionItemId',
        optionItemId !== null ? Math.floor(optionItemId) : null,
      );
      if (kind !== 'all') qs.set('kind', kind);
      if (perItem) qs.set('perItem', '1');
      qs.set('sort', sort);
      qs.set('page', String(page));
      qs.set('limit', String(limit));
      applyDateMode(qs, args);

      interface IItemOptionsToolResponse {
        calendarMode: boolean;
        page: number;
        item?: { id: number; name: string | null; totalUnits: number };
        rows: Array<Record<string, unknown>>;
        truncated?: true;
      }

      const data = await axiosGetData<IItemOptionsToolResponse>(
        `${statsUrl('/item-options')}?${qs.toString()}`,
      );

      return omitNullish({
        page: data.page,
        item: data.item ?? null,
        truncated: data.truncated ?? null,
        rows: (data.rows ?? []).map((row) => omitNullish(row)),
      });
    },
  },
  {
    definition: {
      name: 'stats_itemOptionsSeries',
      description:
        "Sales of specific OPTIONS over time (trend charts): one point per interval per optionItemId with units and revenue, same multiplication semantics as stats_itemOptions. Requires explicit optionItemIds (max 20) - resolve them first via stats_itemOptions or menu_getItem. entities with name:null had no sales in the range (or are not valid options of this store). Use for 'are X sales growing?', 'compare topping Y vs Z by month'.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          optionItemIds: {
            type: 'array',
            items: { type: 'number' },
            minItems: 1,
            maxItems: 20,
          },
          itemId: {
            type: 'number',
            description:
              'Scope to lines of one parent item, e.g. Nutella on brownies only.',
          },
          from: STATS_FROM_PARAM,
          to: STATS_TO_PARAM,
          interval: {
            type: 'string',
            enum: ['auto', 'hourly', 'daily', 'weekly', 'monthly'],
            default: 'auto',
          },
          dateMode: DATE_MODE_PARAM,
        },
        required: ['from', 'optionItemIds'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewStats,
    async execute(args, { deps, scope }) {
      ensureStatsPermission(deps);

      const itemId = getOptionalNumber(args, 'itemId');
      const optionItemIdsRaw = getRequiredNumberArray(args, 'optionItemIds');
      const optionItemIds = Array.from(
        new Set(optionItemIdsRaw.map((n) => Math.floor(n)).filter((n) => n > 0)),
      );

      if (!optionItemIds.length) {
        throw new HostToolError(
          'INVALID_ARGS',
          'optionItemIds must contain positive integers',
          { optionItemIds: optionItemIdsRaw },
        );
      }
      if (optionItemIds.length > 20) {
        throw new HostToolError('INVALID_ARGS', 'Too many optionItemIds (max 20)', {
          count: optionItemIds.length,
        });
      }

      const { from, to, interval } = getStatsRangeParams(
        args,
        deps,
        scope.timeZone,
        getOptionalString(args, 'interval'),
      );

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      setIfDefined(qs, 'to', to);
      setIfDefined(qs, 'interval', interval);
      qs.set('optionItemIds', optionItemIds.join(','));
      setIfDefined(qs, 'itemId', itemId !== null ? Math.floor(itemId) : null);
      applyDateMode(qs, args);

      interface IItemOptionsSeriesToolResponse {
        interval: string;
        timezone: string;
        entities: Record<number, Record<string, unknown>>;
        points: unknown[];
      }

      const data = await axiosGetData<IItemOptionsSeriesToolResponse>(
        `${statsUrl('/item-options/series')}?${qs.toString()}`,
      );

      return omitNullish({
        interval: data.interval,
        timezone: data.timezone,
        entities: data.entities,
        points: data.points,
      });
    },
  },
];
